import type { CompletionRequest } from '@/types';

export type SnippetLanguage = 'curl' | 'csharp' | 'typescript' | 'python';

export const snippetLanguages: { value: SnippetLanguage; label: string }[] = [
  { value: 'curl', label: 'cURL' },
  { value: 'csharp', label: 'C#' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'python', label: 'Python' }
];

/**
 * The request as sent over the wire, minus UI-only noise: attachments become a placeholder
 * comment (base64 blobs make snippets useless) and undefined fields are dropped.
 */
function wireBody(request: CompletionRequest): Record<string, unknown> {
  const body: Record<string, unknown> = {
    supplier: request.supplier,
    modelId: request.modelId,
    promptTemplateName: request.promptTemplateName,
    systemPrompt: request.systemPrompt,
    messages: request.messages.map(m => ({
      role: m.role,
      content: m.content,
      ...(m.attachments?.length
        ? { attachments: [{ kind: 'Image', mediaType: 'image/png', fileName: 'example.png', data: '<base64>' }] }
        : {})
    })),
    maxTokens: request.maxTokens,
    effort: request.effort,
    piiMode: request.piiMode !== 'None' ? request.piiMode : undefined,
    allowUnscannedAttachments: request.allowUnscannedAttachments || undefined,
    tools: request.tools
      ? {
          mcpServers: request.tools.mcpServers,
          maxToolCalls: request.tools.maxToolCalls,
          ...(request.tools.credentials ? { credentials: { '<server-name>': '<your-key>' } } : {})
        }
      : undefined
  };
  return JSON.parse(JSON.stringify(body)); // strips undefined
}

export function buildSnippet(language: SnippetLanguage, request: CompletionRequest, apiBaseUrl: string): string {
  const body = wireBody(request);
  const json = JSON.stringify(body, null, 2);
  const url = `${apiBaseUrl}/completions`;

  switch (language) {
    case 'curl':
      // Single-quoted heredoc-style body keeps the JSON readable and shell-safe.
      return [
        `curl -X POST "${url}" \\`,
        `  -H "Authorization: Bearer $TOKEN" \\`,
        `  -H "Content-Type: application/json" \\`,
        `  -d '${json.replace(/'/g, `'\\''`)}'`
      ].join('\n');

    case 'typescript': {
      // The npm client wants the gateway base (it appends /api/v1 itself).
      const gatewayBase = apiBaseUrl.replace(/\/api\/v1$/, '');
      return [
        `// npm i @keytravel/artificial-intelligence-client`,
        `import { ArtificialIntelligenceClient } from '@keytravel/artificial-intelligence-client';`,
        ``,
        `const client = new ArtificialIntelligenceClient({`,
        `  baseUrl: '${gatewayBase}',`,
        `  getAccessToken: async () => process.env.AI_GATEWAY_TOKEN!  // Entra bearer token`,
        `});`,
        ``,
        `const response = await client.complete(${json});`,
        ``,
        `console.log(response.content, response.usage);`
      ].join('\n');
    }

    case 'csharp': {
      // Raw JSON via HttpClient keeps the snippet dependency-free; the NuGet client is noted.
      return [
        `// Or: dotnet add package KeyTravel.ArtificialIntelligence.Client`,
        `using System.Net.Http.Headers;`,
        `using System.Text;`,
        ``,
        `using var http = new HttpClient { BaseAddress = new Uri("${apiBaseUrl}/") };`,
        `http.DefaultRequestHeaders.Authorization = new AuthenticationHeaderValue("Bearer", token);`,
        ``,
        `const string body = """`,
        json,
        `""";`,
        ``,
        `var response = await http.PostAsync("completions", new StringContent(body, Encoding.UTF8, "application/json"));`,
        `response.EnsureSuccessStatusCode();`,
        `Console.WriteLine(await response.Content.ReadAsStringAsync());`
      ].join('\n');
    }

    case 'python':
      return [
        `import os, requests`,
        ``,
        `response = requests.post(`,
        `    "${url}",`,
        `    headers={"Authorization": f"Bearer {os.environ['AI_GATEWAY_TOKEN']}"},`,
        `    json=${pythonify(json)},`,
        `    timeout=120,`,
        `)`,
        `response.raise_for_status()`,
        `data = response.json()`,
        `print(data["content"], data["usage"])`
      ].join('\n');
  }
}

/** JSON -> Python literal: true/false/null become True/False/None (safe here — values are ours, not user text with those words as standalone tokens outside strings). */
function pythonify(json: string): string {
  return json
    .replace(/\btrue\b(?=([^"]*"[^"]*")*[^"]*$)/g, 'True')
    .replace(/\bfalse\b(?=([^"]*"[^"]*")*[^"]*$)/g, 'False')
    .replace(/\bnull\b(?=([^"]*"[^"]*")*[^"]*$)/g, 'None');
}
